import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { CountryPickerComponent } from '../../../../shared/country-picker';
import { YearPickerComponent } from '../../../../shared/year-picker';
import { AngleDownComponent } from '../../../../icons/angle-down';
import { debounceTime, distinctUntilChanged } from 'rxjs';

interface Subcategory {
  id: string;
  label: string;
  itaLabel?: string; // obbligatoria
}

export interface MainCategory {
  name: string;
  itaName: string;
  subcategories: Subcategory[];
}

export interface Answer {
  main_category?: string; // <-- Add this line
  purchase_category?: string;
  spend_region?: string;
  money_amount?: number;
  money_unit?: string;
  spend_year?: number;
  note?: string;
}

interface Question {
  answers: Answer[] | null;
  emissions: any[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
}

interface Category {
  questions: Question[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
  emissions: any[];
}

@Component({
  selector: 'app-category4-question1',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    PlusComponent,
    DropdownComponent,
    TrashComponent,
    CountryPickerComponent,
    YearPickerComponent,
    AngleDownComponent
  ],
  template: `
    <div class="text-[13px]">
      {{ strings[language].question }}
    </div>
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <!-- Yes / No toggle -->
    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleUsage(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleUsage(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>

    <!-- Purchase Table -->
    <div *ngIf="usage">
      <div class="flex justify-end mb-2 items-center">
        <div
          (click)="addRow()"
          class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
        >
          <app-plus></app-plus>
        </div>
      </div>
      <form [formGroup]="form">
        <div class="space-y-4" formArrayName="purchases">
          <div
            *ngFor="let row of purchases.controls; let i = index"
            [formGroupName]="i"
            class="border rounded-lg p-4"
          >
            <div class="flex justify-end mb-4 cursor-pointer">
              <div
                class="w-1/12 flex justify-end items-center cursor-pointer"
                (click)="removeRow(i)"
              >
                <app-trash></app-trash>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ strings[language].lable_1 }}
                </label>

                <div class="relative" #closeDropdown>
                  <!-- Dropdown trigger -->
                  <div
                    class="p-3 pr-10 rounded-lg border w-full border-gray-300 bg-white cursor-pointer hover:border-[#043B22] focus-within:ring-2 focus-within:ring-[#043B22] h-12 flex items-center relative"
                    (click)="toggleDropdown(i)"
                    [title]="getSelectedLabel(row)"
                  >
                    <span class="text-gray-800 block truncate w-full">
                      {{
                        getSelectedLabel(row) ||
                          strings[language].placeholder_label
                      }}
                    </span>
                    <app-angle-down
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    ></app-angle-down>
                  </div>

                  <!-- Dropdown content -->
                  <ul
                    *ngIf="dropdownOpenStates[i]"
                    class="absolute left-0 right-0 z-20 bg-white border border-gray-300 mt-2 max-h-64 overflow-auto shadow-xl rounded-lg scroll-smooth"
                  >
                    <!-- Search input -->
                    <li
                      class="sticky top-0 bg-white border-b border-gray-200 z-10"
                    >
                      <input
                        type="text"
                        [formControl]="categorySearchControl"
                        placeholder="{{ strings[language].lable_1 }}"
                        class="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#043B22]"
                        (click)="$event.stopPropagation()"
                      />
                    </li>

                    <!-- Categories and subcategories -->
                    <ng-container *ngFor="let category of filteredCategories">
                      <!-- Category title -->
                      <li
                        class="px-4 py-2 text-sm font-semibold text-[#043B22] bg-gray-100 sticky top-[42px] z-0 whitespace-nowrap"
                      >
                        {{
                          language === 'english'
                            ? category.name
                            : category.itaName
                        }}
                      </li>

                      <!-- Subcategory items -->
                      <li
                        *ngFor="let sub of category.subcategories"
                        (change)="saveToLocalStorage()"
                        class="px-4 py-2 text-sm text-gray-700 hover:bg-[#E6F2EC] hover:text-[#043B22] cursor-pointer transition-all truncate"
                        [title]="
                          language === 'english' ? sub.label : sub.itaLabel
                        "
                        (click)="
                          selectCategory(i, sub.id); $event.stopPropagation()
                        "
                      >
                        {{ language === 'english' ? sub.label : sub.itaLabel }}
                      </li>
                    </ng-container>
                  </ul>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_3
                }}</label>
                <app-country-picker
                  [control]="getFormControl(row, 'country')"
                  (change)="saveToLocalStorage()"
                ></app-country-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_4
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  unit=""
                  placeholder="0.00"
                  [control]="getFormControl(row, 'spend')"
                ></app-input>
                <div
                  class="text-red-500"
                  *ngIf="getFormControl(row, 'spend').errors?.['min']"
                >
                  {{ strings[language].lable_4 }}
                  {{ strings[language].minValidation }}
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_5
                }}</label>
                <app-dropdown
                  [options]="currencyOptions"
                  [control]="getFormControl(row, 'currency')"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_6
                }}</label>
                <app-year-picker
                  [control]="getFormControl(row, 'period')"
                ></app-year-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_7
                }}</label>
                <app-input
                  [dollar]="false"
                  unit=""
                  placeholder="{{ strings[language].placeholder_label2 }}"
                  [control]="getFormControl(row, 'note')"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  `
})
export class Category4Question1Component implements OnInit {
  @ViewChild('closeDropdown') dropdownContainer!: ElementRef;
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  dropdownOpenStates: boolean[] = [];
  form!: FormGroup;
  usage = false;

  currencies = ['EUR', 'USD'];
  currencyOptions = this.currencies.map(c => ({ value: c, label: c }));

  strings: any = {
    english: {
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      add_row: 'Add row',
      remove: 'Remove',
      note_placeholder: 'Optional note',
      lable_1: 'Purchase category',
      lable_3: 'Country of production',
      lable_4: 'Net Spend',
      lable_5: 'Currency',
      lable_6: 'Period',
      lable_7: 'Note',
      placeholder_label: 'Select purchase category',
      minValidation: 'must be greater than 0',
      placeholder_label2: 'Optional note'
    },
    italian: {
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      add_row: 'Aggiungi riga',
      remove: 'Rimuovi',
      note_placeholder: 'Nota facoltativa',
      lable_1: 'Categoria di acquisto',
      lable_2: 'Sottocategoria',
      lable_3: 'Paese di produzione',
      lable_4: 'Spesa netta',
      lable_5: 'Valuta',
      lable_6: 'Periodo',
      lable_7: 'Nota',
      placeholder_label: 'Seleziona la categoria di acquisto',
      minValidation: 'deve essere maggiore di 0',
      placeholder_label2: 'Nota facoltativa'
    }
  };

  PRODUCT_CATEGORIES: MainCategory[] = [
    {
      name: 'Raw Materials and Agricultural Products',
      itaName: 'Materie prime e prodotti agricoli',
      subcategories: [
        {
          id: 'arable_farming-type_cereal_grains_not_elsewhere_specified',
          label: 'Cereal grains (not elsewhere specified)',
          itaLabel: 'Cereali (non specificati altrove)'
        },
        {
          id: 'arable_farming-type_crops_not_elsewhere_specified',
          label: 'Crops (not elsewhere specified)',
          itaLabel: 'Colture (non specificate altrove)'
        },
        {
          id: 'arable_farming-type_vegetables_fruit_nuts',
          label: 'Vegetables fruits and nuts',
          itaLabel: 'Verdura, frutta e frutta secca'
        },
        {
          id: 'arable_farming-type_oil_seeds',
          label: 'Oil seeds',
          itaLabel: 'Semi oleosi'
        },
        { id: 'arable_farming-type_wheat', label: 'Wheat', itaLabel: 'Grano' },
        {
          id: 'arable_farming-type_sugar_cane_sugar_beet',
          label: 'Sugar cane and sugar beet',
          itaLabel: 'Canna da zucchero e barbabietola da zucchero'
        },
        {
          id: 'arable_farming-type_plantbased_fibers',
          label: 'Plant-based fibers',
          itaLabel: 'Fibre di origine vegetale'
        },
        {
          id: 'arable_farming-type_paddy_rice',
          label: 'Paddy rice',
          itaLabel: 'Riso grezzo'
        },
        {
          id: 'livestock_farming-type_poultry',
          label: 'Poultry',
          itaLabel: 'Pollame'
        },
        {
          id: 'livestock_farming-type_raw_milk',
          label: 'Raw milk',
          itaLabel: 'Latte crudo'
        },
        {
          id: 'livestock_farming-type_animal_products_not_elsewhere_specified',
          label: 'Animal products (not elsewhere specified)',
          itaLabel: 'Prodotti animali (non specificati altrove)'
        },
        {
          id: 'livestock_farming-type_cattle',
          label: 'Cattle',
          itaLabel: 'Bovini'
        },
        { id: 'livestock_farming-type_pigs', label: 'Pigs', itaLabel: 'Suini' },
        {
          id: 'livestock_farming-type_meat_animals_not_elsewhere_specified',
          label: 'Meat animals (not elsewhere specified)',
          itaLabel: 'Animali da carne (non specificati altrove)'
        },
        {
          id: 'livestock_farming-type_wool_silkworm_cocoons',
          label: 'Wool and silkworm cocoons',
          itaLabel: 'Lana e bozzoli di baco da seta'
        },
        {
          id:
            'fishing_aquaculture-type_fish_other_fishing_products_services_incidental_of_fishing',
          label: 'Fish and other fishing products',
          itaLabel: 'Pesci e altri prodotti della pesca'
        }
      ]
    },
    {
      name: 'Food and Beverage Products',
      itaName: 'Prodotti alimentari e bevande',
      subcategories: [
        {
          id: 'consumer_goods-type_beverages',
          label: 'Beverages',
          itaLabel: 'Bevande'
        },
        {
          id: 'consumer_goods-type_dairy_products',
          label: 'Dairy products',
          itaLabel: 'Prodotti lattiero-caseari'
        },
        {
          id: 'consumer_goods-type_fish_products',
          label: 'Fish products',
          itaLabel: 'Prodotti ittici'
        },
        {
          id: 'consumer_goods-type_food_products_not_elsewhere_specified',
          label: 'Food products (not elsewhere specified)',
          itaLabel: 'Prodotti alimentari (non specificati altrove)'
        },
        {
          id: 'consumer_goods-type_meat_products_beef',
          label: 'Meat products - beef',
          itaLabel: 'Prodotti a base di carne - manzo'
        },
        {
          id: 'consumer_goods-type_meat_products_not_elsewhere_specified',
          label: 'Meat products (not elsewhere specified)',
          itaLabel: 'Prodotti a base di carne (non specificati altrove)'
        },
        {
          id: 'consumer_goods-type_meat_products_pork',
          label: 'Meat products - pork',
          itaLabel: 'Prodotti a base di carne - maiale'
        },
        {
          id: 'consumer_goods-type_meat_products_poultry',
          label: 'Meat products - poultry',
          itaLabel: 'Prodotti a base di carne - pollame'
        },
        {
          id: 'consumer_goods-type_sugar',
          label: 'Sugar',
          itaLabel: 'Zucchero'
        },
        {
          id: 'consumer_goods-type_vegetable_oils_fats',
          label: 'Vegetable oils and fats',
          itaLabel: 'Oli e grassi vegetali'
        },
        {
          id: 'consumer_goods-type_processed_rice',
          label: 'Processed rice',
          itaLabel: 'Riso lavorato'
        },
        {
          id: 'consumer_goods-type_tobacco_products',
          label: 'Tobacco products',
          itaLabel: 'Prodotti del tabacco'
        }
      ]
    },
    {
      name: 'Manufacturing Materials and Components',
      itaName: 'Materiali e componenti per la produzione',
      subcategories: [
        {
          id: 'ceramics-type_ceramic_goods',
          label: 'Ceramic goods',
          itaLabel: 'Prodotti ceramici'
        },
        {
          id: 'chemicals-type_chemicals_not_elsewhere_specified',
          label: 'Chemicals (not elsewhere specified)',
          itaLabel: 'Prodotti chimici (non specificati altrove)'
        },
        {
          id:
            'chemicals-type_chemical_fertilizer_minerals_salt_other_mining_quarrying_products_not_elsewhere_specified',
          label: 'Chemical fertilizer minerals salt and other mining products',
          itaLabel:
            'Fertilizzanti chimici, minerali, sale e altri prodotti minerari'
        },
        { id: 'chemicals-type_bitumen', label: 'Bitumen', itaLabel: 'Bitume' },
        {
          id: 'chemicals-type_lubricants',
          label: 'Lubricants',
          itaLabel: 'Lubrificanti'
        },
        {
          id: 'chemicals-type_white_spirit_sbp',
          label: 'White spirit SBP',
          itaLabel: 'White spirit SBP'
        },
        {
          id: 'glass_products-type_glass_and_glass_products',
          label: 'Glass and glass products',
          itaLabel: 'Vetro e prodotti in vetro'
        },
        {
          id: 'metals-type_basic_iron_steel_of_ferroalloys_first_products',
          label: 'Basic iron steel and ferroalloys',
          itaLabel: 'Ferro, acciaio e ferroleghe di base'
        },
        {
          id: 'metals-type_copper_products',
          label: 'Copper products',
          itaLabel: 'Prodotti in rame'
        },
        {
          id: 'metals-type_aluminium_and_aluminium_products',
          label: 'Aluminium and aluminium products',
          itaLabel: 'Alluminio e prodotti in alluminio'
        },
        {
          id: 'metals-type_lead_zinc_tin_products',
          label: 'Lead zinc and tin products',
          itaLabel: 'Prodotti in piombo, zinco e stagno'
        },
        {
          id: 'metals-type_other_nonferrous_metal_products',
          label: 'Other non‑ferrous metal products',
          itaLabel: 'Altri prodotti in metalli non ferrosi'
        },
        {
          id: 'metals-type_precious_metals',
          label: 'Precious metals',
          itaLabel: 'Metalli preziosi'
        },
        {
          id:
            'metal_products-type_fabricated_metal_products_except_machinery_equipment',
          label: 'Fabricated metal products (except machinery and equipment)',
          itaLabel:
            'Prodotti in metallo lavorato (esclusi macchinari e attrezzature)'
        },
        {
          id: 'plastics_rubber-type_rubber_plastic_products',
          label: 'Rubber and plastic products',
          itaLabel: 'Prodotti in gomma e plastica'
        },
        {
          id: 'plastics_rubber-type_plastics_basic',
          label: 'Basic plastics',
          itaLabel: 'Plastiche di base'
        },
        { id: 'textiles-type_textiles', label: 'Textiles', itaLabel: 'Tessuti' }
      ]
    },
    {
      name: 'Construction and Building Materials',
      itaName: 'Materiali e costruzione',
      subcategories: [
        {
          id:
            'building_materials-type_bricks_tiles_construction_products_in_baked_clay',
          label: 'Bricks tiles and construction products in baked clay',
          itaLabel:
            'Mattoni, piastrelle e materiali da costruzione in argilla cotta'
        },
        {
          id: 'building_materials-type_cement_lime_plaster',
          label: 'Cement lime and plaster',
          itaLabel: 'Cemento, calce e gesso'
        },
        {
          id:
            'timber_forestry-type_products_of_wood_cork_straw_plaiting_materials',
          label: 'Products of wood cork straw and plaiting materials',
          itaLabel: 'Prodotti in legno, sughero, paglia e materiali intrecciati'
        },
        {
          id:
            'timber_forestry-type_products_of_forestry_logging_related_services',
          label: 'Products of forestry and logging',
          itaLabel: 'Prodotti della silvicoltura e del disboscamento'
        },
        {
          id: 'other_materials-type_other_nonmetallic_mineral_products',
          label: 'Other non‑metallic mineral products',
          itaLabel: 'Altri prodotti minerali non metallici'
        },
        {
          id: 'other_materials-type_sand_clay',
          label: 'Sand and clay',
          itaLabel: 'Sabbia e argilla'
        },
        {
          id: 'other_materials-type_secondary_raw_materials',
          label: 'Secondary raw materials',
          itaLabel: 'Materie prime secondarie'
        },
        {
          id: 'other_materials-type_additives_blending_components',
          label: 'Additives and blending components',
          itaLabel: 'Additivi e componenti per miscelazione'
        },
        {
          id: 'other_materials-type_charcoal',
          label: 'Charcoal',
          itaLabel: 'Carbone vegetale'
        },
        {
          id: 'other_materials-type_bkb_peat_briquettes',
          label: 'BKB peat briquettes',
          itaLabel: 'Mattonelle di torba (BKB)'
        },
        { id: 'other_materials-type_peat', label: 'Peat', itaLabel: 'Torba' },
        { id: 'mined_materials-type_stone', label: 'Stone', itaLabel: 'Pietra' }
      ]
    },
    {
      name: 'Paper and Printing Products',
      itaName: 'Prodotti di carta e stampa',
      subcategories: [
        {
          id: 'paper_products-type_paper_paper_products',
          label: 'Paper and paper products',
          itaLabel: 'Carta e prodotti in carta'
        },
        {
          id: 'paper_products-type_printed_matter_recorded_media',
          label: 'Printed matter and recorded media',
          itaLabel: 'Materiale stampato e supporti registrati'
        },
        {
          id: 'paper_products-type_pulp',
          label: 'Pulp',
          itaLabel: 'Polpa di cellulosa'
        }
      ]
    },
    {
      name: 'Machinery and Equipment',
      itaName: 'Macchinari e attrezzature',
      subcategories: [
        {
          id:
            'electrical_equipment-type_electrical_machinery_apparatus_not_elsewhere_specified',
          label: 'Electrical machinery and apparatus (not elsewhere specified)',
          itaLabel:
            'Apparecchiature e macchinari elettrici (non specificati altrove)'
        },
        {
          id:
            'electrical_equipment-type_medical_precision_optical_instruments_watches_clocks',
          label: 'Medical precision and optical instruments watches and clocks',
          itaLabel:
            'Strumenti medicali, di precisione e ottici, orologi e sveglie'
        },
        {
          id:
            'electrical_equipment-type_radio_television_communication_equipment_apparatus',
          label: 'Radio television and communication equipment and apparatus',
          itaLabel: 'Apparecchiature radio, TV e di comunicazione'
        },
        {
          id: 'machinery-type_machinery_equipment_not_elsewhere_specified',
          label: 'Machinery and equipment (not elsewhere specified)',
          itaLabel: 'Macchinari e attrezzature (non specificati altrove)'
        },
        {
          id: 'office_equipment-type_office_machinery_computers',
          label: 'Office machinery and computers',
          itaLabel: 'Macchine per ufficio e computer'
        }
      ]
    },
    {
      name: 'Consumer Goods and Furniture',
      itaName: 'Beni di consumo e mobili',
      subcategories: [
        {
          id:
            'consumer_goods-type_furniture_other_manufactured_goods_not_elsewhere_specified',
          label:
            'Furniture and other manufactured goods (not elsewhere specified)',
          itaLabel:
            'Mobili e altri beni manifatturieri (non specificati altrove)'
        },
        {
          id: 'consumer_goods-type_wearing_apparel_furs',
          label: 'Wearing apparel and furs',
          itaLabel: 'Abbigliamento e pellicce'
        },
        {
          id: 'consumer_goods-type_leather_leather_products',
          label: 'Leather and leather products',
          itaLabel: 'Pelle e prodotti in pelle'
        }
      ]
    },
    {
      name: 'Fertilizers and Agricultural Inputs',
      itaName: 'Fertilizzanti e input agricoli',
      subcategories: [
        {
          id: 'organic_products-type_p_and_other_fertiliser',
          label: 'Phosphorus and other fertilizers',
          itaLabel: 'Fertilizzanti a base di fosforo e altri fertilizzanti'
        },
        {
          id: 'organic_products-type_n_fertiliser',
          label: 'Nitrogen fertilizer',
          itaLabel: 'Fertilizzante azotato'
        }
      ]
    },
    {
      name: 'Mined Materials and Raw Resources',
      itaName: 'Materiali minerari e risorse grezze',
      subcategories: [
        {
          id: 'mined_materials-type_lead_zinc_tin_ores_concentrates',
          label: 'Lead zinc and tin ores and concentrates',
          itaLabel: 'Minerali e concentrati di piombo, zinco e stagno'
        },
        {
          id: 'mined_materials-type_copper_ores_concentrates',
          label: 'Copper ores and concentrates',
          itaLabel: 'Minerali e concentrati di rame'
        },
        {
          id: 'mined_materials-type_precious_metal_ores_concentrates',
          label: 'Precious metal ores and concentrates',
          itaLabel: 'Minerali e concentrati di metalli preziosi'
        },
        {
          id: 'mined_materials-type_other_nonferrous_metal_ores_concentrates',
          label: 'Other non‑ferrous metal ores and concentrates',
          itaLabel: 'Altri minerali e concentrati di metalli non ferrosi'
        },
        {
          id: 'mined_materials-type_iron_ores',
          label: 'Iron ores',
          itaLabel: 'Minerali di ferro'
        },
        {
          id: 'mined_materials-type_nickel_ores_concentrates',
          label: 'Nickel ores and concentrates',
          itaLabel: 'Minerali e concentrati di nichel'
        },
        {
          id: 'mined_materials-type_coking_coal',
          label: 'Coking coal',
          itaLabel: 'Carbone da coke'
        },
        {
          id: 'mined_materials-type_aluminium_ores_concentrates',
          label: 'Aluminium ores and concentrates',
          itaLabel: 'Minerali e concentrati di alluminio'
        },
        {
          id: 'mined_materials-type_lignite_brown_coal',
          label: 'Lignite brown coal',
          itaLabel: 'Lignite (carbone marrone)'
        },
        {
          id: 'mined_materials-type_anthracite',
          label: 'Anthracite',
          itaLabel: 'Antracite'
        },
        {
          id: 'mined_materials-type_subbituminous_coal',
          label: 'Sub‑bituminous coal',
          itaLabel: 'Carbone sub‑bituminoso'
        },
        {
          id: 'mined_materials-type_other_bituminous_coal',
          label: 'Other bituminous coal',
          itaLabel: 'Altro carbone bituminoso'
        },
        {
          id: 'mined_materials-type_patent_fuel',
          label: 'Patent fuel',
          itaLabel: 'Combustibili brevettati'
        },
        {
          id: 'mined_materials-type_uranium_thorium_ores',
          label: 'Uranium and thorium ores',
          itaLabel: 'Minerali di uranio e torio'
        },
        {
          id: 'mined_materials-type_natural_gas_liquids',
          label: 'Natural gas liquids',
          itaLabel: 'Liquidi di gas naturale'
        },
        {
          id: 'mined_materials-type_other_hydrocarbons',
          label: 'Other hydrocarbons',
          itaLabel: 'Altri idrocarburi'
        }
      ]
    }
  ];

  constructor(private fb: FormBuilder) {}
  // Inside your Angular Component:

  subcategoryOptions: any[][] = []; // Track subcategories per row
  categorySearchControl = new FormControl('');
  filteredCategories: any[] = [];
  ngOnInit(): void {
    this.form = this.fb.group({
      purchases: this.fb.array([])
    });
    this.filteredCategories = [...this.PRODUCT_CATEGORIES];

    // Setup search filter
    this.categorySearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: any) => {
        this.filterCategories(searchTerm);
      });

    const categoriesRaw = localStorage.getItem('categories');
    let categories: Category[] = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw) as Category[];

        while (categories.length < 4) {
          categories.push(this.createDefaultCategory());
        }

        if (!categories[3].questions) categories[3].questions = [];
        if (categories[3].questions.length === 0) {
          categories[3].questions.push(this.createDefaultQuestion());
        }

        const answers = categories[3].questions[0].answers;
        if (answers && Array.isArray(answers)) {
          this.usage = true;
          answers.forEach((ans, index) => {
            const group = this.fb.group({
              mainCategory: [ans.main_category || '', Validators.required],
              country: [ans.spend_region || '', Validators.required],
              spend: [
                ans.money_amount || '',
                [Validators.required, Validators.min(0.01)]
              ],
              currency: [ans.money_unit || 'EUR', Validators.required],
              period: [ans.spend_year || '', Validators.required],
              note: [ans.note || '']
            });

            this.purchases.push(group);
            this.dropdownOpenStates.push(false); // Initialize dropdown state for this row
          });
        }
      } catch (e) {
        console.error('Error parsing localStorage categories:', e);
      }
    }

    // Initialize dropdown states array if empty
    if (this.dropdownOpenStates.length === 0 && this.purchases.length > 0) {
      this.dropdownOpenStates = new Array(this.purchases.length).fill(false);
    }

    this.purchases.valueChanges.subscribe(() => {
      this.saveToLocalStorage();
    });
  }

  get purchases(): FormArray {
    return this.form.get('purchases') as FormArray;
  }

  addRow(): void {
    const group = this.fb.group({
      mainCategory: ['', Validators.required],
      country: ['', Validators.required],
      spend: ['', [Validators.required, Validators.min(0.01)]],
      currency: ['EUR', Validators.required],
      period: ['', Validators.required],
      note: ['']
    });
    this.purchases.push(group);
    this.dropdownOpenStates.push(false);
  }

  removeRow(index: number): void {
    this.purchases.removeAt(index);
    this.dropdownOpenStates.splice(index, 1);
  }
  toggleUsage(value: boolean): void {
    this.usage = value;
    if (value && this.purchases.length === 0) {
      this.addRow();
    }
    if (!value) {
      this.purchases.clear();
      this.subcategoryOptions = [];
      this.saveToLocalStorage();
    }
  }

  getFormControl(row: AbstractControl, controlName: string): FormControl {
    return row.get(controlName) as FormControl;
  }

  private _mainCategoriesCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get exiobaseMainCategories() {
    const lang = this.language;
    if (this._mainCategoriesCache?.lang === lang) {
      return this._mainCategoriesCache.opts;
    }
    const opts = this.PRODUCT_CATEGORIES.map(x => ({
      value: x.name,
      label: lang === 'english' ? x.name : x.itaName
    }));
    this._mainCategoriesCache = { lang, opts };
    return opts;
  }

  saveToLocalStorage(): void {
    const purchasesData: Answer[] = this.purchases.controls.map(group => {
      const g = group.value;
      const selectedId = g.mainCategory;
      let mainCategoryInfo = null;
      let subcategoryInfo: Subcategory | null = null;

      for (const category of this.PRODUCT_CATEGORIES) {
        const foundSub = category.subcategories.find(
          sub => sub.id === selectedId
        );
        if (foundSub) {
          mainCategoryInfo = {
            name: category.name,
            itaName: category.itaName
          };
          subcategoryInfo = foundSub;
          break;
        }
      }

      return {
        main_category: g.mainCategory,
        main_category_info: mainCategoryInfo,
        purchase_category_info: subcategoryInfo,
        spend_region: g.country,
        money_amount: g.spend,
        money_unit: g.currency,
        spend_year: g.period,
        note: g.note
      };
    });

    let categories =
      ((this.categoryService.categories() as unknown) as Category[]) || [];

    while (categories.length < 4) {
      categories.push(this.createDefaultCategory());
    }

    if (!categories[3].questions) categories[3].questions = [];
    if (categories[3].questions.length === 0) {
      categories[3].questions.push(this.createDefaultQuestion());
    }

    categories[3].questions[0].answers = this.usage ? purchasesData : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory(): Category {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion(): Question {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }
  dropdownOpenRow: any = null;

  toggleDropdown(index: number) {
    this.dropdownOpenStates = this.dropdownOpenStates.map((state, i) =>
      i === index ? !state : false
    );
  }

  selectCategory(index: number, subcategoryId: string) {
    const row = this.purchases.at(index);
    this.getFormControl(row, 'mainCategory').setValue(subcategoryId);
    this.dropdownOpenStates[index] = false;
  }
  getSelectedLabel(row: any): string | null {
    const selectedId = this.getFormControl(row, 'mainCategory').value;
    for (const category of this.PRODUCT_CATEGORIES) {
      const found = category.subcategories.find(sub => sub.id === selectedId);
      if (found) {
        const label =
          this.language === 'english' ? found.label : found.itaLabel;
        return label ?? null;
      }
    }
    return null;
  }
  filterCategories(searchTerm: string) {
    if (!searchTerm) {
      this.filteredCategories = [...this.PRODUCT_CATEGORIES];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCategories = this.PRODUCT_CATEGORIES.map(category => {
      // Filter subcategories first
      const filteredSubs = category.subcategories.filter(
        sub =>
          sub.label.toLowerCase().includes(term) ||
          sub.id.toLowerCase().includes(term)
      );

      // Only include category if it matches search or has matching subcategories
      if (
        category.name.toLowerCase().includes(term) ||
        filteredSubs.length > 0
      ) {
        return {
          ...category,
          subcategories: filteredSubs
        };
      }
      return null;
    }).filter(category => category !== null);
  }
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.dropdownContainers.some(container =>
      container.nativeElement.contains(event.target)
    );

    if (!clickedInside) {
      this.dropdownOpenStates = this.dropdownOpenStates.map(() => false);
    }
  }
}
